import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useForm from '../use-form';
import FormError from '../FormReponse/FormError';
import FormMessage from '../FormReponse/FormMessage';
import FormSuccess from '../FormReponse/FormSuccess';
import FormRenderer from './FormRenderer'
import ModalFormContext from '../../../store/ModalFormContext';
import { buildFormSubmitPayload } from '../functions/buildFormSubmitPayload';
import { buildCompleteFormPayload } from '../functions/completeFormPayload';

const FormBlockHandler = ({ index, form_id, formIndex, form, forms, classes,  saveHook, setForms}) => {
    const ref = useRef();
    const context = useContext(ModalFormContext)
    const [showSuccess, setShowSuccess] = useState(false);
    const [formValues, setFormValues] = useState(form.values || {});
    const [removeErrors, setRemoveErrors] = useState(false);
    const [uploadingFields, setUploadingFields] = useState({});

    const prevFormValuesRef = useRef(null);
    const lastFormKeyRef = useRef(undefined);

    useEffect(() => {
      const formKey = form.form_id ?? formIndex;
      if (lastFormKeyRef.current !== formKey) {
        lastFormKeyRef.current = formKey;
        prevFormValuesRef.current = form?.values ? JSON.stringify(form.values) : null;
        setFormValues(form.values || {});
        return;
      }

      if (!form?.values) return;
      const valuesKey = JSON.stringify(form.values);
      if (prevFormValuesRef.current === valuesKey) return;
      prevFormValuesRef.current = valuesKey;

      setFormValues((cur) => {
        const next = { ...(cur || {}) };
        Object.keys(form.values).forEach((k) => {
          const serverValue = form.values[k];

          const isMeaningful =
            serverValue !== undefined &&
            serverValue !== null &&
            serverValue !== '' &&
            !(Array.isArray(serverValue) && serverValue.length === 0);

          if (isMeaningful || !(k in next)) {
            next[k] = serverValue;
          }
        });
        return next;
      });
    }, [form?.values, form.form_id, formIndex]);

    useEffect(() => {
      if(forms.length === 1){
        const formElement = document.getElementById('api-form-' + (form.form_id || formIndex))
        if(formElement && formElement.elements.length > 0){
          formElement.elements[0].focus()
        }
      }
    }, [form['form']]);

    useEffect(() => {
      if (form['success']) {
          setShowSuccess(true);
          const timer = setTimeout(() => {
              setShowSuccess(false);
              form['success'] = false;
          }, 1000);
          return () => clearTimeout(timer);
      }
    }, [form['success']]);
  
    const formHandler = useCallback( 
      async (form, reset, additionalArgs) => {
        const slot = form.formIndex
        if (slot === undefined || slot === null) return
        setForms(curForms => {
          const newForms = [...curForms]
          if (form.valid){
            newForms[slot] = {...newForms[slot], ...form, index: slot}
            saveHook && saveHook(newForms, slot)
          }
          else if(form.status === 422){
            newForms[slot] = {...form, index: slot}
          }
          return newForms
        })
        if(form.valid && form.redirect_url){
          // window.location.href = form.redirect_url
        }
        else if(form.valid && form.navigate_url){
          window.location.href = form.navigate_url
        }
    }, [ saveHook])

    const {isLoading : formLoading, error : formError, sendRequest : submitFormData} = useForm(formHandler)

    const submitForm = useCallback(async (data = null, addAnother = false, autoSaved = false, reload = false) => {
      if (Object.values(uploadingFields).some(Boolean)) {
        return;
      }
      let payload = await buildFormSubmitPayload(buildCompleteFormPayload(form, data ?? formValues))
      if (addAnother) {
        payload = { ...payload, __addAnother__: true }
      }
      const reloadForm = (reload && !autoSaved)
      submitFormData({ url: form.action, method: form.method, data: payload, autoSaved, reload: reloadForm, formIndex: formIndex })
    }, [submitFormData, form, formValues, formIndex])

    const debounceTimeout = useRef(null);

    const handleChange = useCallback((e, auto_save = false, reload = false) => {
      if (Object.prototype.hasOwnProperty.call(e, 'uploading')) {
        setUploadingFields((current) => ({
          ...current,
          [e.name]: Boolean(e.uploading)
        }));
      }
      setFormValues({...formValues, [e.name]: e.value})
      if (!form.auto_save && !auto_save && !reload) return;
      var timeout = 1000;
      if(auto_save || reload){
        timeout = 0;
      }
      // Clear any existing timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      // Set a new timeout
      debounceTimeout.current = setTimeout(() => {
        submitForm({...formValues, [e.name]: e.value}, false,  (form.auto_save || auto_save), reload);
      }, timeout);
    }, [form.auto_save, form, formValues, submitForm]);

    const hasUploadingFields = Object.values(uploadingFields).some(Boolean)
    const disabled = (formLoading || hasUploadingFields || (formError && !removeErrors))
    const handleKeyDown = (e) => {
      if (!disabled && e.key === 'Enter' && e.ctrlKey && form.add_another_button) {
        e.preventDefault();
        submitForm(null, true);
      }
    };
    return (
        <>
          <FormError formError={formError} errors={form.errors} setRemoveErrors={setRemoveErrors}/>
          <FormMessage message={form.message}/>
          <form 
            className={`position-relative w-100 ${classes} ${(form.inline) ? 'rest-form-inline' : 'rest-form'}`} 
            ref={ref} 
            id={"api-form-" + (form.form_id || formIndex)} 
            method={form.method} 
            action={form.action} 
            onKeyDown={handleKeyDown}
            >
            {showSuccess && <FormSuccess success={form['success']} />}
              <div className={`${(form.inline) ? 'display-inline' : 'form-wrapper'}`}>
                <FormRenderer onChange={handleChange} index={index} formIndex={formIndex} inline={form.inline} id={"api-form" + formIndex} fields={form.fields} values={formValues} />
              </div>
              {(form.inline) 
                ? 
                  (!form.auto_save)? 
                  <input tabIndex={0} onClick={() => submitForm(null)} className={form.button?.class ? `btn ${form.button.class}` : "btn btn-outline-dark btn-sm save-inline my-auto"} disabled={disabled} type="submit" value={form.button?.text ?? form.button} />  
                  :null
                :
                <div className='bg-white save-button d-flex flex-column' style={{'zIndex':'3'}}>
                  {form.button && form.add_another_button ?
                  <div className="btn-group mx-auto ">
                    {form.button && !form.auto_save ? 
                        form.button.class ?
                          <input tabIndex={0} onClick={() => submitForm(null)} className={`btn ${form.button.class}`} disabled={disabled} type="submit" value={form.button.text}  data-form-type="action"/>  
                          :
                          <input tabIndex={0} onClick={() => submitForm(null)} className="btn btn-outline-primary" disabled={disabled} type="submit" value={form.button}  data-form-type="action"/>  
                        :null
                    }
                    { 
                      (form.add_another_button) ? 
                      <input title="Ctrl + Enter" onClick={e => submitForm(null, true)} className="btn btn-outline-secondary"  type="submit" value={form.add_another_button} disabled={disabled} name="addAnother" data-form-type="action" />
                      :null
                    }
                  </div>
                  :
                  (form.button && !form.auto_save) ? 
                    form.button.class ?
                      <input tabIndex={0} onClick={() => submitForm(null)} className={`btn ${form.button.class}`} disabled={disabled} type="submit" value={form.button.text}  data-form-type="action"/>  
                      :
                      <input tabIndex={0} onClick={() => submitForm(null)} className="btn btn-outline-primary" disabled={disabled} type="submit" value={form.button}  data-form-type="action"/>  
                    :null
                } 
              </div>
            }
          </form>
        </>
    )
}

export default FormBlockHandler;